import React, { useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const TEMPLATES = {
  clients: "name,email,phone,address,status,notes,contact_person,source\n",
  products: "name,sku,category,price,cost,quantity,min_stock,unit\n",
  invoices: "client,client_email,document_number,invoice_date,due_date,subtotal,tax,total,status,notes,payment_terms\n",
};

export default function DataImport() {
  const [importType, setImportType] = useState("clients");
  const [file, setFile] = useState(null);
  const [mappingText, setMappingText] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATES[importType]], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = importType + "-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async (dryRun) => {
    if (!file) {
      toast.error("Choose a CSV file first.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("import_type", importType);
      form.append("mapping", mappingText);
      form.append("dry_run", dryRun ? "true" : "false");
      const res = await api.post("/setup/import", form);
      setReport(res.data);
      if (dryRun) {
        toast.success("Validation complete.");
      } else {
        toast.success("Imported " + res.data.imported + " rows.");
      }
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail ? e.response.data.detail : "Import failed.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#F7F6F2] border-[#E5E2D9]">
      <CardHeader>
        <CardTitle>CSV import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">Import clients, products or past invoices from a CSV file into the current company. Validate first to see a report without importing anything. Files are limited to 5 MB and 2000 rows.</p>
        <p className="text-sm text-slate-500">Imported invoices carry header totals only; line items are not reconstructed. Imported invoices do not create journal entries, even when automatic journals are on.</p>
        <div className="flex gap-2">
          <Button variant={importType === "clients" ? "default" : "outline"} onClick={() => setImportType("clients")}>Clients</Button>
          <Button variant={importType === "products" ? "default" : "outline"} onClick={() => setImportType("products")}>Products</Button>
          <Button variant={importType === "invoices" ? "default" : "outline"} onClick={() => setImportType("invoices")}>Invoices</Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="import-template-btn">Download template</Button>
        </div>
        <div>
          <Label>CSV file</Label>
          <Input type="file" accept=".csv" data-testid="import-file" onChange={(e) => setFile(e.target.files[0])} />
        </div>
        <div>
          <Label>Optional column mapping (JSON)</Label>
          <Textarea value={mappingText} onChange={(e) => setMappingText(e.target.value)} placeholder="Map field names to your CSV headers" data-testid="import-mapping" />
          <p className="text-sm text-slate-500">Fields you leave out fall back to the built-in header aliases.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => runImport(true)} disabled={!file || busy} data-testid="import-validate-btn" className="bg-teal-700 hover:bg-teal-800 rounded-[10px]">Validate</Button>
          <Button onClick={() => runImport(false)} disabled={!file || busy} data-testid="import-run-btn" className="bg-teal-700 hover:bg-teal-800 rounded-[10px]">Import</Button>
        </div>
        {report && (
          <div className="mt-4 space-y-2">
            <p className="text-sm" data-testid="import-report">
              {report.dry_run ? "Validation: " + report.total_rows + " rows checked, " + report.valid_rows + " valid, " + report.errors.length + " skipped." : "Import: " + report.imported + " of " + report.total_rows + " rows imported, " + report.skipped + " skipped."}
            </p>
            {report.errors.length > 0 && (
              <div data-testid="import-errors">
                {report.errors.map((err, i) => (
                  <p key={i} className="text-sm text-red-600">Row {err.row}: {err.message}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
